// import { Box, Typography } from "@mui/material";

import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { StyledTableCell, StyledTableRow } from "Pages/AdminList/ProfessionalUserList";
import React, { useEffect, useState } from "react";

import { Chip, keyframes, styled } from "@mui/material";
import ColorData from "Components/CstmTheme/ColorData";
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch } from "react-redux";
import { setAlertData, startLoading, stopLoading } from "../../Redux/Screen/Action";
import { StatusChip } from "Pages/OrderHistory/OrderHistory";
import PaymentApi from "Service/Payment.api";
import AdminApi from "Service/Admin.api";
import { formatDate, getStatusColorHex } from "Utils/Common";
const pulse = keyframes`
0% { transform: scale(1); }
50% { transform: scale(1.05); }
100% { transform: scale(1); }
`;


interface PaymentHistoryInterface {
  pkPayment: number,
  amount: number,
  currency: string,
  paymentDate: string,
  paymentStatus: string,
  transactionId: string,
  paymentGateway: string,
  paymentType: number,
  caseTypeId: number,
  caseTypeName: string,
  orderTypeId: number,
  orderTypeName: string,
  caseId: number,
  isRefundable: boolean
  clientName: string,
  clientEmail: string,
  clientPhone: string,
  caseNumber: string,
}

const PaymentsHistory: React.FC = () => {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryInterface[] | null>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [refundReason, setRefundReason] = useState("");

  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [paymentToRefund, setPaymentToRefund] = useState<PaymentHistoryInterface | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const dispatch = useDispatch();
  useEffect(() => {
    fetchPaymentHistory(page, rowsPerPage, searchQuery);
  }, [page, rowsPerPage])

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      fetchPaymentHistory(0, rowsPerPage, searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery])

  // No longer needed for server-side filtering
  const filteredHistory = paymentHistory;
  const fetchPaymentHistory = async (currentPage: number, pageSize: number, query: string = "") => {
    dispatch(startLoading({ loading: true, loadtext: "" }));
    const persistedData = localStorage.getItem("persist:root");

    if (persistedData) {
      try {
        const parsedRoot = JSON.parse(persistedData);
        const userObj = JSON.parse(parsedRoot.user);
        const userId = userObj?.myInfo?.id;
        const userRole = userObj?.myInfo?.userType;
        setUserRole(userRole);

        if (!userId || !userRole) {
          console.error("User ID or Role not found in persisted state.");
          dispatch(stopLoading());
          return;
        }

        let response;
        if (userRole === "ADMIN" || userRole === "SUPERADMIN") {
          response = await PaymentApi.getAdminPaymentHistory(currentPage + 1, pageSize, query);
        } else {
          response = await PaymentApi.getUserPaymentHistory(currentPage + 1, pageSize, query);
        }

        if (response.success && response.data) {
          setPaymentHistory(response.data.data || []);
          setTotalCount(response.data.totalCount || 0);
        } else {
          setPaymentHistory([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error("Failed to fetch payment history:", error);
        setPaymentHistory([]);
        setTotalCount(0);
      } finally {
        dispatch(stopLoading());
      }
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  // Update the existing handleRefund function
  const handleRefund = (payment: PaymentHistoryInterface) => {
    setPaymentToRefund(payment);
    setIsRefundModalOpen(true);
  };


  const initiateRefundApi = async () => {
    if (!paymentToRefund) return;

    // 1. Start Loading
    dispatch(startLoading({ loading: true, loadtext: "Processing refund..." }));

    try {
      if (!refundReason.trim()) {
        dispatch(stopLoading());
        dispatch(setAlertData(
          {
            alertMessage: "Please add a reason for the refund.",
            alertVariant: "error",
            openAlert: true
          }
        ))
        return;
      }

      const refundPayload = {
        caseId: paymentToRefund.caseId,
        transactionId: paymentToRefund.transactionId,
        refundReason: refundReason,
        paymentId: paymentToRefund.pkPayment.toString(),
        amount: paymentToRefund.amount,
      };

      const response = await AdminApi.RefundRequest(refundPayload);
      dispatch(setAlertData(
        {
          alertMessage: "Refund processed successfully for Payment ID: " + paymentToRefund.pkPayment,
          alertVariant: "success",
          openAlert: true
        }
      ));


      fetchPaymentHistory(page, rowsPerPage, searchQuery);
      setIsRefundModalOpen(false);
      setPaymentToRefund(null);

    } catch (error) {
      console.error("Refund failed:", error);
      dispatch(stopLoading());
      dispatch(setAlertData(
        {
          alertMessage: "Refund failed. Please check the network logs and server response.",
          alertVariant: "error",
          openAlert: true
        }
      ))
    } finally {
      dispatch(stopLoading());
    }
  };

  const renderTable = () => (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
        mb: 10
      }}
    >
      <TableContainer sx={{ maxHeight: 600, overflow: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableCell>Payment ID</StyledTableCell>
              <StyledTableCell>Case ID</StyledTableCell>
              <StyledTableCell>Transaction ID</StyledTableCell>
              <StyledTableCell>Case Type</StyledTableCell>
              <StyledTableCell>Order Type</StyledTableCell>
              {/* show if role is admin and super admin */}
              {(userRole === "ADMIN" || userRole === "SUPERADMIN") && (
                <StyledTableCell>Client Details</StyledTableCell>
              )}
              <StyledTableCell>Amount</StyledTableCell>
              <StyledTableCell>Payment Type</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Created At</StyledTableCell>
              {userRole === "ADMIN" && <StyledTableCell>Action</StyledTableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredHistory?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                  <Typography variant="h6">No Payments found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              Array.isArray(filteredHistory) &&
              filteredHistory?.map((payment) => (
                <StyledTableRow key={payment.pkPayment}>
                  <TableCell>{payment.pkPayment}</TableCell>
                  <TableCell>{payment.caseNumber ? payment.caseNumber : payment.caseId}</TableCell>
                  <TableCell>{payment.transactionId}</TableCell>
                  <TableCell>{payment.caseTypeName}</TableCell>
                  <TableCell>{payment.orderTypeName}</TableCell>

                  {/* show if role is admin and super admin */}
                  {(userRole === "ADMIN" || userRole === "SUPERADMIN") && (
                    <TableCell>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {payment.clientName}
                        </Typography>
                        <Typography variant="caption" color="text.primary">
                          {payment.clientEmail}
                        </Typography>
                        <Typography variant="caption" color="text.primary">
                          {payment.clientPhone}
                        </Typography>
                      </div>
                    </TableCell>
                  )}

                  <TableCell>{payment.amount}</TableCell>
                  <TableCell>
                    <StatusChip
                      label={payment.paymentType === 1 ? "Charge" : "Refund"}
                      sx={{
                        background: getStatusColorHex(payment.paymentType === 1 ? "Charge" : "Refund").bg,
                        color: getStatusColorHex(payment.paymentType === 1 ? "Charge" : "Refund").text,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      label={payment.paymentStatus}
                      status={payment.paymentStatus}
                      sx={{
                        background: getStatusColorHex(payment.paymentStatus).bg,
                        color: getStatusColorHex(payment.paymentStatus).text,
                      }}
                    />
                  </TableCell>
                  <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                  {userRole === "ADMIN" &&
                    payment.paymentType === 1 &&
                    payment.isRefundable === true && (
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleRefund(payment)}
                          disabled={
                            !(payment.paymentStatus === "Success" ||
                              payment.paymentStatus === "PaymentCompleted")
                          }
                        >
                          Request Refund
                        </Button>
                      </TableCell>
                    )}
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

            {/* Pagination */}
            {(paymentHistory && paymentHistory.length > 0) && (
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      )}
    </Paper>
  );

  const renderCards = () => (
    <Box display="flex" flexDirection="column" gap={2} sx={{ mb: 10 }}>
      {paymentHistory?.length === 0 ? (
        <Typography variant="body1" align="center">
          No data found
        </Typography>
      ) : (
        Array.isArray(paymentHistory) &&
        paymentHistory?.map((payment) => (
          <Card key={payment.pkPayment} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Payment ID: {payment.pkPayment}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Case No: {payment.caseNumber ? payment.caseNumber : payment.caseId}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Transaction ID {payment.transactionId}
              </Typography>
              <Typography variant="body2">
                <strong>Case Type:</strong> {payment.caseTypeName}
              </Typography>
              <Typography variant="body2">
                <strong>Order Type:</strong> {payment.orderTypeName}
              </Typography>
              {/* show if role is admin and super admin */}
              {(userRole === "ADMIN" || userRole === "SUPERADMIN") && (
                <Typography variant="body2">
                  <strong>Client Details:</strong>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {payment.clientName}
                    </Typography>
                    <Typography variant="subtitle2">
                      {payment.clientEmail}
                    </Typography>
                    <Typography variant="subtitle2">
                      {payment.clientPhone}
                    </Typography>
                  </div>
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Amount:</strong> {payment.amount}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {payment.paymentStatus}
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {formatDate(payment.paymentDate)}
              </Typography>
              {userRole === "ADMIN" && payment.paymentType === 1 && payment.isRefundable === true && (
                <Box mt={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => handleRefund(payment)}
                    // Disable if not 'Success' or 'PaymentCompleted'
                    disabled={!(payment.paymentStatus === 'Success' || payment.paymentStatus === "PaymentCompleted")}
                  >
                    Request Refund
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        ))
      )}
      {(paymentHistory && paymentHistory.length > 0) && (
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      )}
    </Box>
  );

  return (
    <Box p={3} width="100%" sx={{
      pb: 30,
    }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom sx={{
        padding: 2,
        borderRadius: 2,
        backgroundColor: theme => ColorData.primary.main,
        color: theme => ColorData.text.secondary,
      }}>
        Payment History
      </Typography>
      <TextField
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search payment history..."
        size="small"
        sx={{ width: { xs: '100%', sm: '300px', padding: 6 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      {isMobile ? renderCards() : renderTable()}


      {/* 🎯 NEW: REFUND CONFIRMATION MODAL */}
      <Dialog
        open={isRefundModalOpen}
        onClose={() => {
          setIsRefundModalOpen(false);
          setRefundReason(""); // Clear reason on close
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: theme.palette.error.main, fontWeight: 'bold' }}>
          Confirm Refund
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "black", mb: 2 }}>
            {/* Are you sure you want to refund the payment 
            of <strong>{paymentToRefund?.currency} {paymentToRefund?.amount}</strong> (Case ID: {paymentToRefund?.pkPayment})? 
            This action cannot be undone. */}
            Add a reason for the refund.
          </DialogContentText>

          {/* Reason Text Area */}
          <TextField
            autoFocus
            margin="dense"
            id="reason"
            label="Reason for Refund"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            placeholder="Please explain why this refund is being initiated..."
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: '#283165', // Your primary color
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#283165', // Your primary color
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setIsRefundModalOpen(false);
              setRefundReason("");
            }}
            sx={{
              textTransform: 'none',
              color: '#283165' // Your primary color
            }}
          >
            No, Cancel
          </Button>
          <Button
            onClick={() => initiateRefundApi()} // Pass reason to API
            variant="contained"
            color="primary"
            disabled={!refundReason.trim()} // Optional: disable if empty
            sx={{
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' }
            }}
          >
            Yes, Initiate Refund
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};



export default PaymentsHistory;